import classNames from 'classnames';
import { type FC, useCallback, useEffect, useMemo } from 'react';

import { Button, ButtonStyle } from '@atlas/components';
import { gamesListRequest } from 'api/casino';
import { ErrorEmptyState, ProvidersEmptyState } from 'components/EmptyStates';
import FavoriteButton from 'components/FavoriteButton';
import Loader from 'components/Loader';
import { useCasinoGames } from 'pages/CasinoPage/casino.context';
import { AuthStatus, useClientProfile } from 'store';
import type { CasinoGroup } from 'utils/casino';
import pushToDataLayer, { btag } from 'utils/dataLayer';
import useLayout from 'utils/layout';
import { Translation } from 'utils/translations';
import { LoadingState } from 'utils/types';
import useSliceRequest from 'utils/useSliceRequest';

import GameTile from '../GameTile';
import styles from './GamesList.module.scss';

type GamesListProps = {
  categories?: string[];
  providers?: string[];
  slice?: number;
  name?: string;
  sortDirection?: 'asc' | 'desc';
  group: CasinoGroup;
};

const GamesList: FC<GamesListProps> = ({
  slice = 50,
  categories,
  name,
  providers,
  group,
  sortDirection,
}) => {
  const layout = useLayout();
  const [{ data: clientData, status }] = useClientProfile();

  const gamesListProps = useMemo(
    () => ({
      query: {
        group,
        name,
        categories,
        providerNames: providers,
        ...(sortDirection && {
          sortDirection,
          sortField: 'name',
        }),
      },
      slice: {
        itemsPerSlice: slice,
      },
    }),
    [group, slice, name, categories, providers, sortDirection],
  );

  useEffect(() => {
    if (status === AuthStatus.resolving) return;

    if (name) {
      pushToDataLayer(
        'casino_search_request',
        {
          ...(clientData && {
            client: {
              id: clientData.client.id,
            },
          }),
          name,
        },
        btag,
      );
    }
  }, [name, clientData, status]);

  const { loadFavoriteGames } = useCasinoGames();

  const { data, loading, loadMore, hasNext, error } = useSliceRequest(
    gamesListRequest,
    gamesListProps,
  );

  const handleLoadMore = useCallback(() => {
    loadMore();
    pushToDataLayer('load_more_games_click', btag);
  }, [loadMore]);

  useEffect(() => {
    loadMore();
  }, [loadMore]);

  useEffect(() => {
    const gameIds = data.map((game) => game.id);

    loadFavoriteGames(gameIds);
  }, [loadFavoriteGames, data]);

  if (error) {
    return <ErrorEmptyState />;
  }

  if (loading === LoadingState.success && data.length === 0) {
    return <ProvidersEmptyState />;
  }

  return (
    <div className={classNames(styles.wrapper, styles[layout])}>
      <div className={styles['games-list']}>
        {data.map(({ id, ...props }) => (
          <div key={id} className={styles['game-item']}>
            <GameTile
              id={id}
              favoriteIcon={<FavoriteButton gameId={id} />}
              {...props}
            />
          </div>
        ))}
      </div>
      {loading === LoadingState.loading && <Loader />}
      {hasNext && loading !== LoadingState.loading && (
        <div className={styles['load-more']}>
          <Button style={ButtonStyle.outline} onClick={handleLoadMore}>
            <Translation message="show_more" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default GamesList;
